<template>
  <a
    :href="resolvedHref"
    :target="newTab ? '_blank' : undefined"
    :rel="newTab ? 'noopener' : undefined"
    class="sso-btn"
    :class="[
      `sso-btn--${variant}`,
      `sso-btn--${size}`,
      { 'sso-btn--block': block, 'sso-btn--disabled': disabled || loading },
    ]"
    :aria-disabled="disabled || loading ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :title="label"
    @click="onClick"
  >
    <i v-if="loading" class="fa-solid fa-spinner fa-spin sso-btn__icon" aria-hidden="true" />
    <i v-else-if="icon" :class="icon" class="sso-btn__icon" aria-hidden="true" />
    <span class="sso-btn__label"><slot>{{ label }}</slot></span>
  </a>
</template>

<script setup>
import { ref, onMounted } from "vue";
import {
  fetchSsoConfig,
  getKeycloakStartUrl,
  ssoHandoffJump,
  isKeycloakAuthType,
} from "@/services/keycloakSso";

const props = defineProps({
  // Явный URL перехода. Если задан — прямой переход по ссылке (без сквозного входа).
  url: { type: String, default: "" },
  // Путь внутри целевой системы для сквозного входа (handoff). Если не задан — главная.
  dest: { type: String, default: "" },
  // Fallback prompt для keycloak start (если handoff недоступен).
  prompt: { type: String, default: "" },
  label: { type: String, default: "Перейти в систему" },
  // Класс иконки, напр. 'fa-solid fa-arrow-up-right-from-square'.
  icon: { type: String, default: "" },
  // Оформление: 'solid' | 'outline' | 'ghost' | 'link'.
  variant: { type: String, default: "solid" },
  // Размер: 'sm' | 'md' | 'lg'.
  size: { type: String, default: "md" },
  block: { type: Boolean, default: false },
  newTab: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["error"]);

const loading = ref(false);

const toTarget = (u) => {
  if (!u) return getKeycloakStartUrl(props.prompt);
  if (/^https?:\/\//i.test(u)) return u;
  return getKeycloakStartUrl(props.prompt, u);
};

const resolvedHref = ref(toTarget(props.url));

onMounted(async () => {
  if (props.url) return;
  const cfg = await fetchSsoConfig();
  resolvedHref.value = toTarget(cfg?.default_url || "");
});

const onClick = async (e) => {
  if (props.disabled || loading.value) {
    e.preventDefault();
    return;
  }
  // Явный url — отдаём навигацию браузеру (учитывает newTab/правый клик).
  if (props.url) return;
  e.preventDefault();
  if (!isKeycloakAuthType()) {
    emit("error", "not_keycloak");
    return;
  }
  loading.value = true;
  try {
    const jumped = await ssoHandoffJump(props.dest);
    if (jumped) return; // уходим со страницы — лоадер оставляем
    if (props.newTab) {
      window.open(resolvedHref.value, "_blank", "noopener");
      loading.value = false;
    } else {
      window.location.assign(resolvedHref.value);
    }
  } catch (err) {
    loading.value = false;
  }
};
</script>

<style scoped>
.sso-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.2s ease, transform 0.12s ease, filter 0.2s ease;
  white-space: nowrap;
  line-height: 1.2;
}

.sso-btn__icon {
  font-size: 1em;
}

/* размеры */
.sso-btn--sm {
  padding: 6px 12px;
  font-size: 13px;
}
.sso-btn--md {
  padding: 9px 16px;
  font-size: 14px;
}
.sso-btn--lg {
  padding: 13px 22px;
  font-size: 15px;
}

.sso-btn--block {
  display: flex;
  width: 100%;
}

/* solid */
.sso-btn--solid {
  background: var(--sso-accent, #0060fe);
  color: #fff;
  box-shadow: 0 8px 20px -10px rgba(0, 96, 254, 0.6);
}
.sso-btn--solid:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
}

/* outline */
.sso-btn--outline {
  background: #fff;
  color: var(--sso-accent, #0060fe);
  border-color: var(--sso-accent, #0060fe);
}
.sso-btn--outline:hover {
  background: var(--sso-accent-soft, #eff6ff);
}

/* ghost */
.sso-btn--ghost {
  background: var(--sso-accent-soft, #eff6ff);
  color: var(--sso-accent, #0060fe);
}
.sso-btn--ghost:hover {
  filter: brightness(0.98);
}

/* link */
.sso-btn--link {
  background: transparent;
  color: var(--sso-accent, #0060fe);
  padding-left: 0;
  padding-right: 0;
  border-radius: 0;
}
.sso-btn--link:hover {
  text-decoration: underline;
}

.sso-btn--disabled {
  opacity: 0.55;
  pointer-events: none;
  box-shadow: none;
}
</style>
